// ClientsPage.js
import React, { useEffect, useState, useRef } from 'react';
import { Spin, Table, Input, Button, Modal, Switch, Tabs, message } from 'antd';
import { SearchOutlined, email,PhoneOutlined } from '@ant-design/icons';
import { auth } from '../../Auth';
import { firebase } from '../../utilities';
import RegisterClient from './RegisterClient';
import { Timestamp, serverTimestamp } from 'firebase/firestore'


const { TabPane } = Tabs;

const ALLOWED_ORGANIZATION_ID = 'cy8qzbSmrzRdpuHKxV10lTg004i2';

export default function Clients() {
  console.log("Clients component rendered");
  const [authorizedOrg, setAuthorizedOrg] = useState(null);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [show, setShow] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [togglingId, setTogglingId] = useState(null);

  // Reusable search properties for columns
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
          }}
          icon="SearchOutlined"
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => {
            clearFilters();
            setSearchText('');
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current && searchInput.current.select(), 100);
      }
    },
  });

  // Define table columns according to the updated requirements
  const clientColumns = [
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <div>{record.phone}</div>
        </div>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: (created) => {
        if (created && created.seconds) {
          const date = new Date(created.seconds * 1000);
          return date.toLocaleDateString();
        }
        return '';
      },
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles) => (roles ? roles.join(', ') : ''),
    },
    {
      title: 'Third Party Inspector',
      dataIndex: 'third_party_inspector',
      key: 'third_party_inspector',
      render: (inspector) => (inspector ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Switch
          checked={!record.disabled}
          loading={togglingId === record.id}
          checkedChildren="Active"
          unCheckedChildren="Disabled"
          onChange={() => toggleDisabled(record)}
        />
      ),
    },
  ];

  // Fetch organizations from Firestore ordered by created (latest first)
  async function fetchOrganizations() {
    setLoadingClients(true);
    try {
      const querySnapshot = await firebase
        .firestore()
        .collection('organizations')
        .orderBy('created', 'desc')
        .get();
      const orgData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          key: doc.id,
          id: doc.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          created: data.created,
          roles: data.roles,
          third_party_inspector: data.third_party_inspector,
          disabled: data.disabled || false,
        };
      });
      setClients(orgData);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoadingClients(false);
    }
  }

  function toggleDisabled(client) {
    const willDisable = !client.disabled;
    const action = willDisable ? 'disable' : 'enable';
    Modal.confirm({
      title: `${willDisable ? 'Disable' : 'Enable'} client?`,
      content: `Are you sure you want to ${action} "${client.name}"?`,
      okText: willDisable ? 'Disable' : 'Enable',
      okType: willDisable ? 'danger' : 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setTogglingId(client.id);
        try {
          await firebase
            .firestore()
            .collection('organizations')
            .doc(client.id)
            .update({
              disabled: willDisable,
              updatedBy: { id: auth.sunkaizenUser.id , name: auth.sunkaizenUser.name },
              updatedAt: serverTimestamp()
            });
          message.success(`Client ${willDisable ? 'disabled' : 'enabled'} successfully.`);
          fetchOrganizations();
        } catch (err) {
          console.error('Error updating client:', err);
          message.error('Failed to update client status.');
        } finally {
          setTogglingId(null);
        }
      },
    });
  }

  useEffect(() => {
    return auth.sunkaizenUserSubscribe((user) => {
      setAuthorizedOrg(user?.organization?.id === ALLOWED_ORGANIZATION_ID);
    });
  }, []);

  useEffect(() => {
    if (auth.sunkaizenUser) {
      fetchOrganizations();
    }
  }, []);

  if (authorizedOrg === null) return <Spin size="large" />;
  if (!authorizedOrg) return <h1 className="page-header padding-3">You are not authorized to view this page</h1>;

  return (
    <div
      className="flex-column align-start full-width"
      style={{ padding: '4rem', background: 'rgb(244, 246, 248)' }}
    >
      <div className="flex-row" style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-header">Clients</h1>
        </div>
        <div>
          <Button type="primary" onClick={() => setShow(true)}>
            Add Client
          </Button>
        </div>
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ width: '100%' }}>
        <TabPane tab="Active" key="active" />
        <TabPane tab="Disabled" key="disabled" />
      </Tabs>
      {loadingClients ? (
        <Spin size="large" />
      ) : (
        <Table
          dataSource={clients.filter(c => activeTab === 'active' ? !c.disabled : c.disabled)}
          columns={clientColumns}
          pagination={{ pageSize: 10 }}
          style={{ width: '100%', backgroundColor: '#fff' }}
        />
      )}
      <Modal
        width={'80%'}
        destroyOnClose={true}
        footer={null}
        onCancel={() => setShow(false)}
        visible={show}
      >
        <RegisterClient
          onSuccess={() => { setShow(false); fetchOrganizations(); }}
          onCancel={() => setShow(false)}
        />
      </Modal>
    </div>
  );
}
